import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UntypedFormArray, UntypedFormGroup, ValidatorFn } from '@angular/forms';

import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { Store } from '@ngrx/store';


import { environment } from '../../../../environments/environment';
import { CalendarSchedule } from '../../models/user/user.model';
import { DatesService } from '../dates/dates.service';
import {
  CalendarGroupValidationData,
  CalendarValidationData,
  Invitation
} from '../../models/invitation/invitation.model';
import * as ModalActions from '../../../ngrx/modal/actions/modal.action';
import { ToastComponent } from '../../../ui/toast/toast.component';
import { IAppState } from '../../../ngrx';



@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private apiUrl = environment.apiUrl;
  private companyUrl = this.apiUrl + 'companies/';

  constructor(
    private http: HttpClient,
    private store: Store<IAppState>,
    private translate: TranslateService,
    private datesService: DatesService
  ) {
  }

  public connectToGoogle(): Observable<any> {
    return this.http.post(`${this.apiUrl}calendars/connect-google`, {});
  }

  public connectToOutlook(): Observable<any> {
    return this.http.post(`${this.apiUrl}calendars/oauth/microsoft/connect`, {});
  }

  public disconnectFromGoogle(data?: {
    cancel_pending: boolean
  }): Observable<any> {
    const params = new HttpParams({
      fromObject: data
    });

    return this.http.delete(`${this.apiUrl}calendars/disconnect-google`, { params });
  }

  public disconnectFromOutlook(data?: {
    cancel_pending: boolean
  }): Observable<any> {
    const params = new HttpParams({
      fromObject: data
    });

    return this.http.delete(`${this.apiUrl}calendars/oauth/microsoft/disconnect`, { params });
  }

  public updateSchedule(scheduleId: number, schedule: CalendarSchedule): Observable<any> {
    return this.http.put(`${this.apiUrl}calendars/schedule/${scheduleId}`, schedule);
  }

  public validateCalendarConnection(data: {
    user_id: number,
    application_ids?: string,
    folder_id?: number
  }): Observable<CalendarValidationData> {
    const params = new HttpParams({
      fromObject: data
    });

    return this.http.get<CalendarValidationData>(`${this.apiUrl}calendars/validate-connection/`, { params });
  }

  /**
   * check that all attenders have connected calendars
   * @param data
   */
  public validateGroupCalendarConnection(data: {
    attender_ids: string
  }): Observable<CalendarGroupValidationData[] | null> {
    const params = new HttpParams({
      fromObject: data
    });

    return this.http.get<CalendarGroupValidationData[] | null>(`${this.apiUrl}calendars/validate-attender-connections/`, { params });
  }

  /**
   * get those employees of the company who has activated calendar integration in settings
   * @param companyId number - company to get employees of
   * @param search string - string to search between employees
   * @param jobId number - job to get employees of
   */
  public getCalendarEmployees(companyId: number, search: string, jobId: number): Observable<any> {
    const paramsObj = {
      has_calendar_active: '1',
      search,
      job_id: jobId
    };

    if (search && search.trim().length === 0) {
      delete paramsObj.search;
    }

    const params = new HttpParams({
      fromObject: paramsObj
    });

    return this.http.get(`${this.companyUrl}${companyId}/employees/attenders/`, { params });
  }

  /**
   * get available time that means intersection of time from setting schedule and employee calendar
   * @param {Object} data - dates, timezone, user_ids
   * @param {string} data.dates - dates to get available time for
   * @param {string} data.timezone - timezone of user
   * @param {string} data.user_ids - ids of users to get available time for
   */
  public getAvailableTime(data: {
    dates: string,
    timezone: string,
    user_ids: string
  }): Observable<any> {
    const { dates, timezone, user_ids } = data;
    const params = {
      dates,
      timezone,
      user_ids
    };

    return this.http.get(`${this.apiUrl}calendars/schedule/available-timeslots`, { params });
  }

  /**
   * get invitations in draft status
   */
  public getPendingInvitations(): Observable<any> {
    return this.http.get(`${this.apiUrl}calendars/invitations/pending`);
  }

  /**
   * get invitations in draft status within one application
   * @param applicationId number - application to get invitation for
   */
  public getApplicationPendingInvitations(applicationId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}calendars/invitations/pending/${applicationId}`);
  }

  /**
   * get all invitations in draft status within one assessment folder
   * @param folderId number - assessment folder to get invitation for
   */
  public getFolderPendingInvitations(folderId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}calendars/invitations/pending?folder=${folderId}`);
  }

  /**
   * cancel all invitations in draft status within one application
   * @param applicationId number - application to cancel invitations for
   */
  public cancelApplicationPendingInvitations(applicationId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}calendars/invitations/pending/${applicationId}/cancel`, {});
  }

  /**
   * send array of invitations at one time
   * @param groupInvitations Invitation[] - invitations to send
   */
  public sendGroupInvitations(groupInvitations: Invitation[]): Observable<any> {
    return this.http.post(`${this.apiUrl}calendars/invitations/send-group`, groupInvitations);
  }

  /**
   * cancel all invitations in draft status within one assessment folder
   * @param data.folderId number - assessment folder to cancel invitations for
   * @param data.application_ids string - comma separated application ids to cancel invitations for
   */
  public cancelGroupPendingInvitations(data: {
    folder_id?: number,
    application_ids?: number[]
  }): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}calendars/invitations/pending/cancel`, data);
  }

  /**
   * set hasOverlapTime error if timeslot intersect with other timeslots of day,
   * if hasInvalidTime error timeslot is invalid date
   * or hasRevertTime error start of timeslot is later that the end
   */
  public timeValidValidator(): ValidatorFn {
    return (array: UntypedFormArray): {[key: string]: any} | null => {
      (array as UntypedFormArray).controls.forEach((ctrl, i) => {
        const hasOverlapTime = !!array.value.find((el, j) => (i !== j) && this.areOverlapping(el, ctrl.value)) ;

        const valid = this.datesService.momentTransform(ctrl.value.start, 'HH:mm').isValid &&
                      this.datesService.momentTransform(ctrl.value.end, 'HH:mm').isValid;

        const hasRevertTime = ctrl.value.start && ctrl.value.end && valid && ctrl.value.start > ctrl.value.end;

        if (hasOverlapTime || hasRevertTime) {
          ctrl.setErrors({
            ...(hasOverlapTime && {hasOverlapTime}),
            ...(hasRevertTime && {hasRevertTime})
          });
        } else {
          ctrl.setErrors(null);
        }
      });

      return null;
    };
  }

  /**
   * check if time is invalid
   * check when user finished type which means length is more than 5
   */
  public isInvalid(): ValidatorFn {
    return (group: UntypedFormGroup): {[key: string]: any} | null => {
      const time = `${group.controls.hour.value}:${group.controls.minute.value}`;
      const fullTimeLength = 5;

      return time.length > fullTimeLength && !this.datesService.momentTransform(time, 'HH:mm').isValid ? {
        hasInvalidTime: true
      } : null;
    };
  }

  /**
   * check if any of form controls have errors
   */
  public controlsValidator(): ValidatorFn {
    return (group: UntypedFormGroup): {[key: string]: any} | null => {
      const hasErrors = !!(group.get('schedule') as UntypedFormArray).controls.find(ctrl =>
        (ctrl.get('timeslots') as UntypedFormArray).controls.find(c => !!c.errors)
      );
      return hasErrors ? {controlsInvalid: true} : null;
    };
  }

  /**
   * check that at least one day is checked
   */
  public dayCheckedValidator(): ValidatorFn {
    return (group: UntypedFormGroup): {[key: string]: any} | null => {
      const checked = !!(group.get('schedule') as UntypedFormArray).controls.find(ctrl => ctrl.value.checked);
      return !checked ? {noDayChecked: true} : null;
    };
  }

  /**
   * check if two timeslots have intersections
   * @param a - first timeslot
   * @param b - second timeslot
   */
  public areOverlapping(a, b): boolean {
    if (b.start < a.start) {
      return b.end > a.start;
    }
    else {
      return b.start < a.end;
    }
  }

}
